import { useEffect, useState, type CSSProperties } from 'react';

/**
 * The app's loading language — one motion motif per kind of wait, all of it
 * going still under `prefers-reduced-motion` (the animations live in styles.css).
 *
 * `Skeleton` — a warm sheen sweeping a sunken panel: content that is on its way,
 * laid out in the shape of what will land so nothing jumps when it does.
 * `Thinking` — bubbles rising off a pan, the one green voice: a model that is
 * thinking (recipe generation, import). It cycles a line so a several-second
 * wait has something to read.
 * `DotsMini` — the same bubbles, button-sized, for an inline busy control.
 * `Loading` — a calm full-screen placeholder for a plain fetch, faded in only
 * once a wait is real, so a fast local read never flashes it.
 */

export function Skeleton({
  w,
  h,
  radius,
  className,
  style,
}: {
  w?: number | string;
  h?: number | string;
  radius?: number | string;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <span
      className={`skeleton${className ? ` ${className}` : ''}`}
      style={{ width: w, height: h, borderRadius: radius, ...style }}
      aria-hidden
    />
  );
}

export function DotsMini() {
  return (
    <span className="dots-mini" aria-hidden>
      <span />
      <span />
      <span />
    </span>
  );
}

export function Thinking({ messages }: { messages: string[] }) {
  const [i, setI] = useState(0);
  const count = messages.length;

  // Cycle the reassurance line; a single message just sits there. Depending on
  // the count (a number) keeps this stable across the parent's re-renders.
  useEffect(() => {
    if (count <= 1) return;
    const id = setInterval(() => setI((n) => (n + 1) % count), 2200);
    return () => clearInterval(id);
  }, [count]);

  const idx = count ? i % count : 0;
  return (
    <div className="thinking" role="status" aria-label={messages[0] ?? 'Bezig…'}>
      <span className="thinking-dots" aria-hidden>
        <span />
        <span />
        <span />
      </span>
      {/* Remounts on each swap so the fade replays. */}
      <span className="thinking-label" key={idx} aria-hidden>
        {messages[idx]}
      </span>
    </div>
  );
}

export function Loading() {
  return (
    <div className="loading-screen" role="status" aria-label="Laden…">
      {[0, 1, 2].map((i) => (
        <div className="loading-card" key={i}>
          <Skeleton w="52%" h={14} />
          <Skeleton w="82%" h={12} />
        </div>
      ))}
    </div>
  );
}
